import type { FC, ReactNode } from 'react';
import { Accordion, AccordionItem, Button, Stack, Tile } from '@carbon/react';
import { ArrowRight, Download, Locked, Renew } from '@carbon/icons-react';
import { Analytics } from '@carbon/pictograms-react';

import { MODELOS_DO_PDF_DO_DIAGNOSTICO } from '@maestra/core/constants/realNarrative';
import type { DadosDoDiagnostico } from '../../../../logica/desenvolvimento/useDiagnostico';
import { ModeloDoPdfModal } from '../../../padroes/ModeloDoPdf';
import { PaginaDeModulo } from '../../../padroes/PaginaDeModulo';
import { Secao } from '../../../padroes/Secao';
import { Vazio } from '../../../padroes/Vazio';
import { RelatorioReal } from './RelatorioReal';
import styles from './Diagnostico.module.scss';

// A PÁGINA DO DIAGNÓSTICO REAL (Desenvolvimento): o relatório, com baixar o PDF e refazer ao lado
// do perfil; para quem ainda não tem planejamento, a chamada para começá-lo com a Nyta (com o vídeo
// do método); no fim, quem assina a metodologia e como nasce o diagnóstico.
//
// Uma página do Desenvolvimento é como um módulo: sem cabeçalho de página (o `h1` é escondido, e o
// nome está aceso no SideNav).

/**
 * O corpo do diagnóstico (o relatório, a chamada do planejamento, quem assina e o método). É o mesmo
 * na página do Desenvolvimento e na entrega do fluxo de criar o perfil (`telas/criarArtista`).
 */
export const CorpoDoDiagnostico: FC<{
  d: DadosDoDiagnostico;
  relatorio: NonNullable<DadosDoDiagnostico['relatorio']>;
  refazer?: ReactNode;
  /** As dimensões em sequência, e não em abas (a entrega). */
  emSequencia?: boolean;
}> = ({ d, relatorio, refazer, emSequencia }) => (
    <div className={styles.pagina}>
      <RelatorioReal
        r={relatorio}
        emSequencia={emSequencia}
        acoes={(
          <>
            <Button kind='secondary' size='md' renderIcon={Download} disabled={d.pdf.baixando} onClick={d.pdf.abrir}>{d.pdf.rotulo}</Button>
            {/* No legado, refazer mora no aviso de versão: aqui seria a mesma ação duas vezes. */}
            {!relatorio.avisoDeVersao && refazer}
          </>
        )}
        refazerDoAviso={d.refazer ? { rotulo: d.refazer.rotulo, abrir: d.refazer.abrir } : undefined}
      />
      {/* O banco de modelos do PDF (o mesmo do branding): o Baixar abre, o Gerar baixa. */}
      <ModeloDoPdfModal p={{ ...d.pdf, gerando: d.pdf.baixando, exportar: d.pdf.baixar }} textos={MODELOS_DO_PDF_DO_DIAGNOSTICO} kicker='Diagnóstico' />

      {d.planejamento && (
        <Tile className={styles.chamada} data-chamada-do-planejamento=''>
          <Stack gap={5}>
            <h2 className={styles.tituloDaChamada}>{d.planejamento.titulo}</h2>
            <p className={styles.texto}>{d.planejamento.apoio}</p>
            <div className={styles.video}>
              <iframe
                src={d.planejamento.video.url}
                title={d.planejamento.video.titulo}
                allow='accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share'
                allowFullScreen
                loading='lazy'
              />
            </div>
            <div><Button kind='primary' size='lg' className={styles.botaoDaChamada} renderIcon={ArrowRight} onClick={d.planejamento.abrir}>{d.planejamento.botao}</Button></div>
            <p className={styles.apoio}>{d.planejamento.nota}</p>
          </Stack>
        </Tile>
      )}

      <Secao id='diagnostico-assina' titulo='Quem assina'>
        <div className={styles.assinatura}>
          <p className={styles.nomeDeQuemAssina}>{d.assinatura.nome}</p>
          <p className={styles.apoio}>{d.assinatura.papel}</p>
          {d.assinatura.paragrafos.map((p) => <p key={p} className={styles.texto}>{p}</p>)}
          <p className={styles.destaque}>{d.assinatura.destaque}</p>
        </div>
      </Secao>

      <Accordion>
        <AccordionItem title={d.metodologia.titulo}>
          <Stack gap={5}>
            {d.metodologia.intro.map((p) => <p key={p} className={styles.texto}>{p}</p>)}
            <dl className={styles.metodo}>
              {d.metodologia.dimensoes.map((m) => (
                <div key={m.letra} className={styles.itemDoMetodo}>
                  <dt><span className={styles.letraDoMetodo}>{m.letra}</span>{m.titulo}</dt>
                  <dd className={styles.texto}>{m.texto}</dd>
                </div>
              ))}
            </dl>
            <p className={styles.texto}>{d.metodologia.fim}</p>
          </Stack>
        </AccordionItem>
      </Accordion>
    </div>
);

export const Diagnostico: FC<{ d: DadosDoDiagnostico }> = ({ d }) => {
  const refazer = d.refazer && (
    <Button kind='ghost' size='md' renderIcon={d.refazer.travado ? Locked : Renew} onClick={d.refazer.abrir}>{d.refazer.rotulo}</Button>
  );

  if (!d.relatorio) {
    return (
      <PaginaDeModulo titulo='Diagnóstico REAL'>
        <Vazio
          pictograma={Analytics}
          titulo='Sua carreira ainda não foi medida'
          texto='O Diagnóstico REAL mede quatro dimensões da carreira e mostra em qual dos 16 perfis ela está hoje.'
          acao={d.refazer && <Button kind='primary' size='md' renderIcon={d.refazer.travado ? Locked : ArrowRight} onClick={d.refazer.abrir}>{d.refazer.rotulo}</Button>}
        />
      </PaginaDeModulo>
    );
  }

  return (
    <PaginaDeModulo titulo='Diagnóstico REAL'>
      <CorpoDoDiagnostico d={d} relatorio={d.relatorio} refazer={refazer} />
    </PaginaDeModulo>
  );
};
